import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useMe } from '../api/me';
import { useIntegrations } from '../api/integrations';
import { InlineError } from '../components/InlineError';
import { Skeleton } from '../components/Skeleton';
import { ProviderRow } from '../components/ProviderRow';
import { RelativeTime } from '../components/RelativeTime';
import { useToast } from '../components/toastContext';
import type { IntegrationProvider, IntegrationsStatus } from '../types';

const KNOWN_PROVIDERS: ReadonlyArray<{ id: IntegrationProvider; label: string }> = [
  { id: 'github', label: 'GitHub' },
  { id: 'atlassian', label: 'Atlassian' },
];

const CONNECTED_PARAM = 'connected';
const INTEGRATION_ERROR_PARAM = 'integration_error';

/**
 * Wire contract with a1ec's BrowserIntegrationError enum (A1EE-692): browser-launched
 * integration flows that fail redirect to `/me?integration_error=<code>` instead of
 * replacing the SPA with a raw JSON error document. Unknown codes fall back to the
 * generic message (never echoed raw), so a1ec can add codes before the SPA learns them.
 */
const INTEGRATION_ERROR_MESSAGES: Record<string, string> = {
  connect_in_progress:
    'A connection attempt is already completing — give it a few seconds, then check the status below.',
  invalid_state: 'That sign-in link was already used or had expired. Start the connection again.',
  callback_failed: 'The provider returned an error while connecting. Try again.',
  flow_busy: 'Another credential operation for this integration is in flight. Try again shortly.',
};
const GENERIC_INTEGRATION_ERROR = 'Connecting the integration failed. Try again.';

export function MePage() {
  useIntegrationResultToast();
  return (
    <div className="space-y-10 px-6 py-10">
      <ProfileSection />
      <IntegrationsSection />
    </div>
  );
}

/**
 * Toast the outcome of an integration OAuth round trip (A1EE-692).
 *
 * a1ec redirects a *browser* setup flow back to `/me?connected=<provider>` on success and
 * `/me?integration_error=<code>` on failure. Without this the page is byte-identical before
 * and after a reconnect — same "Connected" pill, same "Reconnect" button — so users can't
 * tell success from a no-op and re-click, and every re-auth invalidates the previous access
 * token. The error path matters just as much: the connect debounce and callback failures
 * would otherwise be invisible dead ends.
 *
 * Both params are optional in both directions: an a1ec that doesn't send them yet is a plain
 * `/me` load, so the two sides deploy in either order. An unrecognized provider value is
 * dropped rather than rendered; an unrecognized error code gets the generic message.
 */
function useIntegrationResultToast() {
  const [params, setParams] = useSearchParams();
  const { showToast } = useToast();
  const connected = params.get(CONNECTED_PARAM);
  const integrationError = params.get(INTEGRATION_ERROR_PARAM);
  // Fire once per navigation. Stripping the params is what normally prevents a repeat, but
  // that's an async state update and React 19 StrictMode double-invokes mount effects on the
  // same fiber — so gate on a ref, which survives that remount. A genuine second attempt
  // arrives as a full-page navigation (ProviderRow uses window.location.assign), which
  // remounts the page and resets the ref.
  const toastedFor = useRef<string | null>(null);

  useEffect(() => {
    if (connected === null && integrationError === null) return;
    const marker = `${connected ?? ''}|${integrationError ?? ''}`;
    if (toastedFor.current === marker) return;
    toastedFor.current = marker;

    const label =
      connected === null ? undefined : KNOWN_PROVIDERS.find((p) => p.id === connected)?.label;
    if (label) showToast(`${label} credentials refreshed`);
    if (integrationError !== null) {
      showToast(INTEGRATION_ERROR_MESSAGES[integrationError] ?? GENERIC_INTEGRATION_ERROR, 'error');
    }

    // Strip the params either way so a reload doesn't re-toast and the URL stays clean.
    // `replace` keeps the OAuth bounce out of the history stack. This re-runs the effect
    // with both params null, which returns above.
    const next = new URLSearchParams(params);
    next.delete(CONNECTED_PARAM);
    next.delete(INTEGRATION_ERROR_PARAM);
    setParams(next, { replace: true });
  }, [connected, integrationError, params, showToast, setParams]);
}

function ProfileSection() {
  const { data: me, error, isPending, refetch } = useMe();
  return (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-slate-900">Profile</h2>
      {isPending && (
        <Skeleton variant="rows" rows={4} rowClassName="h-5 w-72" className="space-y-2" />
      )}
      {error && (
        <InlineError heading="Couldn't load profile" error={error} onRetry={() => void refetch()} />
      )}
      {me && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
          <dt className="text-slate-500">Email</dt>
          <dd className="font-semibold text-slate-900">{me.email}</dd>
          <dt className="text-slate-500">User ID</dt>
          <dd className="font-mono text-xs text-slate-700">{me.id}</dd>
          <dt className="text-slate-500">Account created</dt>
          <dd className="text-slate-700">
            <RelativeTime iso={me.createdAt} />
          </dd>
          <dt className="text-slate-500">Account updated</dt>
          <dd className="text-slate-700">
            <RelativeTime iso={me.updatedAt} />
          </dd>
        </dl>
      )}
    </section>
  );
}

function IntegrationsSection() {
  const { data, error, isPending, refetch } = useIntegrations();
  return (
    <section>
      <h2 className="mb-1 text-lg font-semibold text-slate-900">Integrations</h2>
      <p className="mb-4 text-sm text-slate-600">
        Choose the provider account A1EC should use for your work.
      </p>
      {error ? (
        <InlineError
          heading="Couldn't load integrations"
          error={error}
          onRetry={() => void refetch()}
        />
      ) : (
        <div className="grid gap-3">
          {KNOWN_PROVIDERS.map(({ id, label }) => (
            <ProviderRow
              key={id}
              provider={id}
              label={label}
              connected={isPending ? null : isConnected(data, id)}
              isPending={isPending}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function isConnected(data: IntegrationsStatus | undefined, provider: IntegrationProvider): boolean {
  if (!data) return false;
  return data.integrations.some((i) => i.provider === provider && i.connected);
}
